import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [sidebarMenuItems, setSidebarMenuItems] = useState([]);
  const [propertyDetailsTabs, setPropertyDetailsTabs] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load user data from localStorage on app start
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const savedSidebarItems = localStorage.getItem('sidebarMenuItems');
    const savedPropertyTabs = localStorage.getItem('propertyDetailsTabs');
    
    if (savedUser && savedSidebarItems && savedPropertyTabs) {
      setUser(JSON.parse(savedUser));
      setSidebarMenuItems(JSON.parse(savedSidebarItems));
      setPropertyDetailsTabs(JSON.parse(savedPropertyTabs));
      setIsAuthenticated(true);
    }
  }, []);

  const setUserPermissions = (loginResponse) => {
    console.log('setUserPermissions called with:', loginResponse); // Debug log
    
    const { user, sidebarMenuItems: sidebarItemsString, propertyDetailsTabs: propertyTabsString } = loginResponse;
    
    // Parse comma-separated strings into arrays
    const sidebarItems = sidebarItemsString ? sidebarItemsString.split(',').map(item => item.trim()) : [];
    const propertyTabs = propertyTabsString ? propertyTabsString.split(',').map(item => item.trim()) : [];
    
    console.log('Parsed data:', { user, sidebarItems, propertyTabs }); // Debug log
    
    // Update state
    setUser(user);
    setSidebarMenuItems(sidebarItems);
    setPropertyDetailsTabs(propertyTabs);
    setIsAuthenticated(true);
    
    // Persist to localStorage
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('sidebarMenuItems', JSON.stringify(sidebarItems));
    localStorage.setItem('propertyDetailsTabs', JSON.stringify(propertyTabs));
    
    console.log('Data stored in localStorage:', {
      user: localStorage.getItem('user'),
      sidebarItems: localStorage.getItem('sidebarMenuItems'),
      propertyTabs: localStorage.getItem('propertyDetailsTabs')
    }); // Debug log
  };

  const getUserPermissions = () => {
    return {
      user,
      sidebarMenuItems,
      propertyDetailsTabs,
      isAuthenticated
    };
  };

  const hasMenuAccess = (menuItemId) => {
    return sidebarMenuItems.includes(menuItemId);
  };

  const hasTabAccess = (tabId) => {
    return propertyDetailsTabs.includes(tabId);
  };

  const logout = () => {
    setUser(null);
    setSidebarMenuItems([]);
    setPropertyDetailsTabs([]);
    setIsAuthenticated(false);
    
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('sidebarMenuItems');
    localStorage.removeItem('propertyDetailsTabs');
  };

  const value = {
    user,
    sidebarMenuItems,
    propertyDetailsTabs,
    isAuthenticated,
    setUserPermissions,
    getUserPermissions,
    hasMenuAccess,
    hasTabAccess,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
